
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { getLocale, shallowEqual, useIntl, useSelector } from 'umi';
import moment from 'moment';
import CardBox from "@/pages/dashboard/components/CardBox";
import BarChart from "@/pages/dashboard/components/Chart/components/BarChart";
import LineChart from "@/pages/dashboard/components/Chart/components/LineChart";
import MiniTable from "@/pages/dashboard/components/MiniTable";
import { addChartLabel, xAxisHighLight, xAxisLabelFormat } from "@/pages/dashboard/components/Chart/_config/plug";
import { FilterContext } from '@/pages/dashboard/components/PageContainer/context';
import { formatExeclNum, formatPercentNum, handleDownload, formatFilterValues, formatExeclNormalPercent, formatExeclDecimal } from "@/pages/dashboard/utils/utils";
import { getApiData } from '@/pages/dashboard/services/common';
import { PERIODTIMEOBJ } from '@/pages/dashboard/_config/constant';
import { thousandthFormat } from '@/pages/home/utils/common';
import { getEchartTooltipText } from '@/pages/dashboard/components/EchartsBubble';
import { useWeekDay } from '@/pages/dashboard/utils/customHook';
import './index.scss';

const DataRangeTrendCo = (props: any) => {
  const { selectedMetric, selectedValue, config } = props;
  const { api } = config;
  const contextObj = useContext(FilterContext);
  const { gameCode } = useSelector((state: any) => {
    return {
      gameCode: state?.dashboard?.currentGame?.game_code,
    };
  }, shallowEqual);
  const { getWeekDay } = useWeekDay();
  const { formatMessage } = useIntl(); // 中英切换方法
  const isEN = getLocale() === 'en-US';
  const { filterLoading, filterVal = {} } = contextObj || {};
  const { timeType = 'Day' } = filterVal;
  const { metricKey, type, decimal, enName, cnName, preUnit, endUnit } = selectedMetric || {};
  const title = isEN ? enName : cnName;
  const subTitle2 = formatMessage({ id: 'common_Accumulated' });
  const [metricData, setMetricData] = useState([]);
  const [aggregateData, setAggregateData] = useState({});
  const [iseye, setIseye] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (filterLoading) return;
    getMetricData();
  }, [filterLoading, filterVal, selectedValue]);

  const getMetricData = () => {
    setLoading(true);
    const { timeType, diff, rangeTime, ...filters } = filterVal || {};
    const params: any = {
      game_code: gameCode,
      period: PERIODTIMEOBJ?.[timeType],
      filters: formatFilterValues(filters),
      start_time: rangeTime?.[0],
      end_time: rangeTime?.[1],
      metrics: [selectedValue],
      is_aggregate: true,
    };
    getApiData(api, params).then(res => {
      setMetricData(res?.trend_list || []);
      setAggregateData(res?.aggregate || {});
      setLoading(false);
      setError(false);
    }).catch(err => {
      setMetricData([]);
      setAggregateData({});
      setLoading(false);
      setError(true);
    });
  };

  const getVal = (value: any) => {
    return type === 'percent' ? formatPercentNum(value, decimal) : thousandthFormat(value, type === 'int' ? 0 : decimal, true, '-');
  };

  const handleAxisFormat = (value: moment.MomentInput) => {
    return timeType === "Month" ? moment(value).format('YYYY-MM') : moment(value).format('MM-DD');
  };

  const {
    xAxisData = [],
    legendData = [],
    barSeries = [],
    lineSeries = [],
    isNodata = true,
    table = {
      option: [],
      data: [],
      downloadData: [],
    }
  } = useMemo(() => {
    const xAxisData: any[] = [];
    const legendData: any[] = [title];
    const barSeries: any[] = [];
    const lineSeries: any[] = [];
    const tableData: any[] = [];
    const tableDownloadData: any[] = [];
    let isNodata: boolean = true;
    const seriesObj: any = {
      name: title,
      data: [],
    };
    metricData?.map((item: any) => {
      xAxisData?.push(item?.date);
      const value = item?.metrics?.[metricKey]?.value;
      seriesObj.data?.push(value);
      if (value) isNodata = false;
      const date = timeType === 'Month' ? moment(item?.date).format('YYYY-MM') : moment(item?.date).format('YYYY-MM-DD');
      tableData?.push({
        date,
        value: getVal(value)
      });
      tableDownloadData?.push({
        date,
        value: type === 'percent' ? formatExeclNormalPercent(value) : type === 'int' ? formatExeclNum(value) : formatExeclDecimal(value)
      });
    });
    barSeries?.push({
      type: 'bar',
      barGap: '5%',
      ...seriesObj
    });
    lineSeries?.push({
      type: 'line',
      symbol: 'circle',
      ...seriesObj
    });

    const option = [
      {
        key: 'date',
        header: formatMessage({ id: 'overview_table_date' }),
        align: 'left',
        fixed: 'left',
        width: 160,
        render: (cvm: any) => {
          const weekDay = getWeekDay(cvm?.date);
          const isSunOrSat = weekDay === 'Sun' || weekDay === 'Sat' || weekDay === '周日' || weekDay === '周六';
          return <>
            <span className='table-text'>{cvm?.date}</span>
            {timeType !== 'Month' && <span className={isSunOrSat ? 'text-week-b' : 'text-week'}>{weekDay}</span>}
          </>;
        },
      },
      {
        key: 'value',
        header: title,
        align: 'right',
      }
    ];
    return {
      xAxisData,
      legendData,
      barSeries,
      lineSeries,
      isNodata,
      table: {
        option,
        data: tableData,
        downloadData: tableDownloadData
      }
    };
  }, [metricData, isEN]);

  const subTitleCom = useMemo(() => {
    const value = aggregateData?.[selectedValue]?.value || '-';
    const pre = value !== '-' ? (preUnit || '') : '';
    const end = value !== '-' ? (endUnit || '') : '';
    return (
      <div className='sub-title-wrapper'>
        <div className='dbd-metrics-card-sub-title'>{pre}{getVal(value)}{end}</div>
        <div className='dbd-metrics-card-sub-title2'> {subTitle2}</div>
      </div>
    );

  }, [aggregateData, selectedValue,subTitle2]);

  const formatterFunc = (params: any) => {
    const date = timeType === 'Month' ? moment(params?.[0]?.axisValue).format('YYYY-MM') : moment(params?.[0]?.axisValue).format('YYYY-MM-DD');
    const data = params?.map((item: { seriesName: any; value: number; color: any; }) => {
      return {
        name: item.seriesName,
        metricsNum: getVal(item?.value),
        color: item.color
      };
    });
    return getEchartTooltipText({
      title: date,
      data,
    });
  };

  const renderList = [{
    type: 'eyes',
    svgType: iseye ? 'open-eyes' : 'eyes-closed',
    content: iseye ? formatMessage({ id: 'common_show_values' }) : formatMessage({ id: 'common_hide_values' }),
    isHideCom: true,
  },
  {
    type: 'barChart',
    svgType: 'column-chart',
    content: formatMessage({ id: 'common_bar_chart' }),
    component: <BarChart
      height={340}
      legendData={xAxisData?.length > 0 ? legendData : []}
      xAxisData={xAxisData || []}
      series={xAxisData?.length > 0 ? barSeries : []}
      formatterFunc={formatterFunc}
      setOtherConfigFunc={(opt: any) => {
        opt = xAxisLabelFormat(opt, { xAxisLabelFormat: handleAxisFormat });
        opt = addChartLabel(opt, {
          formatter: (params: any) => {
            return getVal(params?.value);
          },
          show: iseye,
          formatNum: (value: any) => {
            return getVal(value);
          }
        });
        opt = xAxisHighLight(opt, {
          formatter: (params: { value: string; }) => {
            return handleAxisFormat(params?.value);
          }
        });
        if (type === 'percent') {
          opt.yAxis.axisLabel.formatter = (value: number) => formatPercentNum(value, 2);
        }
        return opt;
      }}
    />
  },
  {
    type: 'lineChart',
    svgType: 'discount-chart',
    content: formatMessage({ id: 'common_line_chart' }),
    component: <LineChart
      height={340}
      legendData={xAxisData?.length > 0 ? legendData : []}
      xAxisData={xAxisData || []}
      series={xAxisData?.length > 0 ? lineSeries : []}
      formatterFunc={formatterFunc}
      setOtherConfigFunc={(opt: any) => {
        opt = xAxisLabelFormat(opt, { xAxisLabelFormat: handleAxisFormat });
        opt = addChartLabel(opt, {
          formatter: (params: any) => {
            return getVal(params?.value);
          },
          show: iseye,
          formatNum: (value: any) => {
            return getVal(value);
          }
        });
        opt = xAxisHighLight(opt, {
          formatter: (params: { value: string; }) => {
            return handleAxisFormat(params?.value);
          },
        });
        if (type === 'percent') {
          opt.yAxis.axisLabel.formatter = (value: number) => formatPercentNum(value, 2);
        }
        return opt;
      }}
    />
  },
  {
    type: 'sheet',
    svgType: 'sheet',
    content: formatMessage({ id: 'common_table' }),
    component: <MiniTable
      height={340}
      loading={false}
      tableOption={table?.option}
      tableList={table?.data}
    />
  },];



  return <CardBox
    className='register-table'
    titleCom={title}
    subTitleCom={subTitleCom}
    height={340}
    bordered={false}
    renderList={renderList}
    defaultAvtive={2}
    setCurrentDataType={(type: any) => {
      if (type === 'eyes') {
        setIseye(!iseye);
      }
    }}
    downloadClick={async () => {
      handleDownload(table, title);
    }}
    isLoading={loading}
    isError={error}
    isNodata={isNodata}
  />;
};

export default DataRangeTrendCo;